import { Injectable } from '@angular/core';
import { ResetUnits } from '../data/portal-products';
import { environment } from './../../environments/environment';
import { HttpService } from './http.service';
import { UserStore } from './store.service';
import { Subject, BehaviorSubject } from 'rxjs';

@Injectable()
export class AccountHttp {
  constructor(protected httpService: HttpService) {}

  signOutFromServer = async () => await this.httpService.post('/account/logout');
  requestCaptcha = async () => await this.httpService.post('/captcha/request');
  downTimeNotice = async () => await this.httpService.get('/account/down-time-notification');
  getCurrentUserProfile = async () => await this.httpService.get('/account/me');

  getAccessToken = async (user: { username: string; password: string; captchaResponse: string; captchaAttempts: string }) => {
    if (environment.useOAuth) {
      let { access_token } = await this.httpService.postForm(
        '/oauth/token',
        Object.assign(user, { grant_type: 'password', scope: 'trust' }),
        {
          apiPath: '',
          contentType: 'application/x-www-form-urlencoded',
          oAuthHeader: this.httpService.oAuthHeader,
        }
      );
      return access_token;
    } else {
      let { authenticationToken } = await this.httpService.postForm('/account/signin', user);

      return authenticationToken;
    }
  };

  getSocialAccessToken = async (socialUser: { provider: string; token: string }) => {
    let { access_token } = await this.httpService.postForm('/account/social-login', socialUser);

    return access_token;
  };

  sendVerificationCodes = async (verificationFields) =>
    await this.httpService.postForm('/account/send-signup-verification-codes', verificationFields);
  requestResetPassword = async (userInfo: { nic: string; passport: string }) =>
    await this.httpService.postForm('/account/request-reset-password/', userInfo);
  resetPassword = async (userInfo: { nic: string; passport: string; verificationCode: string; password: string }) =>
    await this.httpService.postForm('/account/reset-password', userInfo);
  verifySignUpCode = async (verification) => await this.httpService.postForm('/account/verify-signup-code', verification);

  signUp = async (user) => await this.httpService.postForm('/account/signup', user);

  sendPasswordReset = async (verificationFields) => await this.httpService.postForm('/account/send-password-reset', verificationFields);
  changePassword = async (password) => await this.httpService.postForm('/account/send-change-password', password);
  sendEmailConfirmation = async (email) => await this.httpService.postForm('/account/send-email-confirmation', email);
  sendMobileConfirmation = async (data) => await this.httpService.postForm('/account/send-mobile-number-confirmation-code', data);
  verifyEmailConfirmation = async (verificationCode) =>
    await this.httpService.postForm('/account/verify-email-confirmation', verificationCode);
  verifyMobileConfirmation = async (formData) => await this.httpService.postForm('/account/verify-mobile-number', formData);

  getUserSettings = async () => await this.httpService.get('/account/consolidated-report-settings');
  updateUserSettings = async (userSettings) => await this.httpService.post('/account/consolidated-report-settings', userSettings);

  isValidPasswordResetToken = async (resetToken) =>
    await this.httpService.postForm('/account/verify-password-reset', {
      resetToken,
    });

  confirmResetPassword = async ({ resetToken, password }) =>
    await this.httpService.post('/account/confirm-password-reset', {
      resetToken,
      password,
    });

  confirmSignupEmail = async (inviteToken) =>
    await this.httpService.post('/account/validate-password-reset', {
      inviteToken,
    });
  updateProfile = async (user) => await this.httpService.post('/account/profile', user);

  updateDiscalimer = async (val) => await this.httpService.postForm('/account/research-portal-disclaimer', val);
  updatePortalDiscalimer = async () => await this.httpService.put('/users/accept-portal-disclaimer');

  updateAvatar = async (avatar) => await this.httpService.post('/account/update-avatar-url', avatar);

  resendConfirmationEmail = async (email: string) => await this.httpService.post('/xxxxxxxxxx', { email });

  isEmailInUse = async (email: string) => await this.httpService.post('/commonops/is-email-used', { email });

  userSavedData = async (userId) => await this.httpService.get(`/users/details${(userId && '?userId=' + userId) || ''}`);

  getAccountBalance = async () => await this.httpService.get('/account/me/account-balance');

  getAccountFinBalances = async (customerId) =>
    await this.httpService.get(`/account/me/financial/balance-summary${(customerId && '?investorCode=' + customerId) || ''}`);

  getFinanceClientDetails = async (accountType, partyId, division) =>
    await this.httpService.get(
      `/account/me/financial/customer-details?accountType=${accountType}&partyId=${partyId}&productType=${division}`
    );

  getPortfolioBalanceCount = async (accountType, partyId, division) =>
    await this.httpService.get(`/account/me/financial/portfolio/${division}/count?accountType=${accountType}&partyId=${partyId}`);

  getPortfolioBalances = async (accountType, partyId, division, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/portfolio/${division}?accountType=${accountType}&partyId=${partyId}&page=${page}&size=${size}${
        (sort && '&sort=' + sort) || ''
      }`
    );

  getTransactionsBalanceCount = async (accountType, customerId, division, stockCode, fromDate, toDate) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}/count?accountType=${accountType}${
        (customerId && '&investorCode=' + customerId) || ''
      }${(stockCode && '&stockCode=' + stockCode) || ''}&fromDate=${fromDate}&toDate=${toDate}`
    );
  getTransactionsBalances = async (accountType, customerId, division, stockCode, fromDate, toDate, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}?accountType=${accountType}${(customerId && '&investorCode=' + customerId) || ''}${
        (stockCode && '&stockCode=' + stockCode) || ''
      }&fromDate=${fromDate}&toDate=${toDate}&page=${page}&size=${size}${(sort && '&sort=' + sort) || ''}`
    );

  getCALSCompanies = async (accountType, partyId, division) =>
    await this.httpService.get(`/account/me/financial/portfolio/${division}/companies?accountType=${accountType}&partyId=${partyId}`);

  getCALITransactionsBalanceCount = async (accountType, partyId, division, fundCode, subAccount, fromDate, toDate) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}/count?accountType=${accountType}&partyId=${partyId}&fundCode=${fundCode}${
        (subAccount && '&subAccount=' + subAccount) || ''
      }&fromDate=${fromDate}&toDate=${toDate}`
    );

  getCALITransactionsBalances = async (accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}?accountType=${accountType}&partyId=${partyId}&fundCode=${fundCode}${
        (subAccount && '&subAccount=' + subAccount) || ''
      }&fromDate=${fromDate}&toDate=${toDate}&page=${page}&size=${size}${(sort && '&sort=' + sort) || ''}`
    );

  getCALIEarnings = async (accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/earnings/${division}?accountType=${accountType}&partyId=${partyId}&fundCode=${fundCode}${
        (subAccount && '&subAccount=' + subAccount) || ''
      }&fromDate=${fromDate}&toDate=${toDate}&page=${page}&size=${size}${(sort && '&sort=' + sort) || ''}`
    );

  getCALIGainCount = async (accountType, partyId, division, fundCode, subAccount, fromDate, toDate) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}/gain/count?accountType=${accountType}&partyId=${partyId}&fundCode=${fundCode}&subAccount=${subAccount}&fromDate=${fromDate}&toDate=${toDate}`
    );

  getCALIGain = async (accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/transaction/${division}/gain?accountType=${accountType}&partyId=${partyId}&fundCode=${fundCode}&subAccount=${subAccount}&fromDate=${fromDate}&toDate=${toDate}&page=${page}&size=${size}${
        (sort && '&sort=' + sort) || ''
      }`
    );

  getCALTPortfolioBalanceCount = async (accountType, partyId, division, instrumentActType) =>
    await this.httpService.get(
      `/account/me/financial/portfolio/${division}/count?accountType=${accountType}&partyId=${partyId}&instrumentActType=${instrumentActType}`
    );

  getCALTPortfolioBalances = async (accountType, partyId, division, instrumentActType, page, size, sort) =>
    await this.httpService.get(
      `/account/me/financial/portfolio/${division}?accountType=${accountType}&partyId=${partyId}&instrumentActType=${instrumentActType}&page=${page}&size=${size}${
        (sort && '&sort=' + sort) || ''
      }`
    );

  getCALTPortfolioBalanceTotals = async (accountType, partyId) =>
    await this.httpService.get(`/account/me/financial/portfolio/calt/balances?accountType=${accountType}&partyId=${partyId}`);

  getSignUpPermission = async () => await this.httpService.get('/account/is-product-signup-enabled');

  getPortfolioGraphData = async () => await this.httpService.get('/account/portfolio-graph-data');
  getProductPortfolioGraphData = async (product, accType, crmId) =>
    await this.httpService.get(
      `/account/product-portfolio-graph-data?portfolioType=${product}&accountType=${accType}&crmCode=${crmId || ''}`
    );

  getAccountSummary = async (crmId, accType, displayAllFunds) =>
    await this.httpService.get(
      `/account/me/financial/cali-portfolio-summary?crmId=${crmId}&accountType=${accType}&displayAllFunds=${displayAllFunds}`
    );

  getAnnualizedYield = async (crmId, fundCode, subAcc, accType, date) =>
    await this.httpService.get(
      `/ut-request/get-annualized-yield?accountType=${accType}&fundCode=${fundCode}&subAccount=${subAcc}&partyId=${crmId}&date=${date}`
    );

  isAvailableUser = async (userId, type) =>
    await this.httpService.get(
      `/account/check-availability?nic=${(type === 'NIC' && userId) || ''}&passport=${(type === 'Passport' && userId) || ''}`
    );
  getDayendDelayedId = async () => await this.httpService.get(`/account/day-end-delayed-funds`);

  saveBrowserInfo = async (data) => {
    await this.httpService.post(`/users/save-user-device-details`, data);
  };
  getClientRequest = async () => {
    return await this.httpService.get(`/client-request`);
  };
  submitClientRequest = async (type, accType, formData) => {
    await this.httpService.post(`/client-request?requestType=${type}&accountType=${accType}`, formData);
  };

  getLatestTransaction = async (crmCode, division) => {
    return await this.httpService.get(`/account/me/financial/latest-transaction?crmCode=${crmCode}&division=${division}`);
  };
  getLedgerSummary = async (fromDate, toDate, investorCode?) => {
    return await this.httpService.get(`/margin-trade/ledger-summary?fromDate=${fromDate}&toDate=${toDate}&investorCode=${investorCode}`);
  };

  getPortfolioBalance = async (date, investorCode?) => {
    return await this.httpService.get(`/margin-trade/portfolio?date=${date}&investorCode=${investorCode}`);
  };

  getMarginTradePortfolio = async (date, investorCode?) => {
    return await this.httpService.get(`/margin-trade/portfolio?date=${date}&investorCode=${investorCode}`);
  };
  sendLoginOTP = async (emailOnly?) => this.httpService.get(`/users/send-login-otp${emailOnly && '?emailOnly=' + emailOnly}`);
  savePIN = async (data, isCrmxUser = false) => {
    return await this.httpService.post(`/account/save-pin?isCrmxUser=${isCrmxUser}`, data);
  };
  updatePIN = async (data, isCrmxUser = false) => {
    return await this.httpService.put(`/account/update-pin?isCrmxUser=${isCrmxUser}`, data);
  };
  validatePIN = async (pin, isCrmxUser = false) => {
    return await this.httpService.postForm(`/account/validate-pin?isCrmxUser=${isCrmxUser}`, { pin: pin });
  };

  getUserNickNameAvailability = async (nickName: string) => {
    return await this.httpService.get(`/users/nick-name-availability?nickName=${nickName}`);
  };
  addNickName = async (data: any) => {
    return await this.httpService.post(`/users/add-nick-name`, data);
  };
  getServerTime = async () => {
    return await this.httpService.get(`/account/current-date-time`);
  }
}

@Injectable()
export class AccountService {
  private _currentUser: any;
  userSavedDataLoadingPromise;
  userSavedDataLoaded;
  private accountFinBalances;
  emitChange$: Subject<any> = new BehaviorSubject<any>(null);
  getSavedId$: Subject<any> = new BehaviorSubject<any>(null);
  getLoginSuccess$: Subject<any> = new BehaviorSubject<any>(null);
  resetDashboard$: Subject<any> = new BehaviorSubject<any>(null);
  updateProfile$: Subject<any> = new BehaviorSubject<any>(null);
  switchToTransaction$: Subject<any> = new BehaviorSubject<any>(null);
  switchPendingHistory$: Subject<any> = new BehaviorSubject<any>(null);
  selectCorporate$: Subject<any> = new BehaviorSubject<any>(null);
  setDetailViewId$: Subject<any> = new BehaviorSubject<any>(null);
  selectedAccountType$: Subject<any> = new BehaviorSubject<any>(null);
  toggleAccountBalanceView$: Subject<any> = new BehaviorSubject<any>(null);
  setGetTransactionDate$: Subject<any> = new BehaviorSubject<any>(null);
  selectedDivision$: Subject<any> = new BehaviorSubject<any>(null);
  emit(value: any) {
    this.emitChange$.next(value);
  }
  get emitChange(): BehaviorSubject<any> {
    return this.emitChange$ as BehaviorSubject<any>;
  }
  emitId(value: any) {
    this.getSavedId$.next(value);
  }
  get getSavedId(): BehaviorSubject<any> {
    return this.getSavedId$ as BehaviorSubject<any>;
  }

  emitLoginStatus(value: any) {
    this.getLoginSuccess$.next(value);
  }
  get getLoginSuccess(): BehaviorSubject<any> {
    return this.getLoginSuccess$ as BehaviorSubject<any>;
  }

  emitProfile(value: any) {
    this.updateProfile$.next(value);
  }
  get emitprofileUpdate(): BehaviorSubject<any> {
    return this.updateProfile$ as BehaviorSubject<any>;
  }

  emitStockId(value: any) {
    this.switchToTransaction$.next(value);
  }
  get switchToTransaction(): BehaviorSubject<any> {
    return this.switchToTransaction$ as BehaviorSubject<any>;
  }

  emitTransactionDate(value: any) {
    this.setGetTransactionDate$.next(value);
  }
  get setGetTransactionDate(): BehaviorSubject<any> {
    return this.setGetTransactionDate$ as BehaviorSubject<any>;
  }

  emitPendingHistory(value: any) {
    this.switchPendingHistory$.next(value);
  }
  get switchPendingHistory(): BehaviorSubject<any> {
    return this.switchPendingHistory$ as BehaviorSubject<any>;
  }

  emitAccountType(value: any) {
    this.selectedAccountType$.next(value);
  }
  get selectedAccountType(): BehaviorSubject<any> {
    return this.selectedAccountType$ as BehaviorSubject<any>;
  }

  emitCorpId(value: any) {
    this.selectCorporate$.next(value);
  }
  get selectCorporate(): BehaviorSubject<any> {
    return this.selectCorporate$ as BehaviorSubject<any>;
  }

  setDetailView(value: any) {
    this.setDetailViewId$.next(value);
  }
  get setDetailViewId(): BehaviorSubject<any> {
    return this.setDetailViewId$ as BehaviorSubject<any>;
  }
  resetDashboard(value: any) {
    this.resetDashboard$.next(value);
  }
  get emitResetAll(): BehaviorSubject<any> {
    return this.resetDashboard$ as BehaviorSubject<any>;
  }

  emitAccountBalanceToggle(value: any) {
    this.toggleAccountBalanceView$.next(value);
  }
  get toggleAccountBalanceView(): BehaviorSubject<any> {
    return this.toggleAccountBalanceView$ as BehaviorSubject<any>;
  }
  emitSelectedDivision(value: any) {
    this.selectedDivision$.next(value);
  }
  get selectedDivision(): BehaviorSubject<any> {
    return this.selectedDivision$ as BehaviorSubject<any>;
  }

  get currentUser() {
    return this._currentUser;
  }
  setCurrentUser(user) {
    this._currentUser = user;
  }
  constructor(protected http: AccountHttp) {}

  async currentUserAsync() {
    if (this._currentUser) {
      return this._currentUser;
    } else {
      await this.loadCurrentUserFromServer();
      return this._currentUser;
    }
  }
  async updateDiscalimer(val) {
    await this.http.updateDiscalimer(val);
  }
  async updatePortalDiscalimer() {
    await this.http.updatePortalDiscalimer();
  }

  async loadCurrentUserFromServer() {
    const user = await this.http.getCurrentUserProfile();
    this._currentUser = user;
    return this._currentUser;
  }

  async isLoggedIn(): Promise<boolean> {
    if (this._currentUser) {
      return true;
    }
    await this.loadCurrentUserFromServer();
    if (this._currentUser) {
      return true;
    }
    if (localStorage['atk']) {
      return true;
    }
    return false;
  }

  async requestCaptcha() {
    return await this.http.requestCaptcha();
  }
  async downTimeNotice() {
    return await this.http.downTimeNotice();
  }

  async signIn({ username, password, captchaResponse, captchaAttempts }) {
    this.userSavedDataLoaded = undefined;
    this.userSavedDataLoadingPromise = undefined;
    UserStore.reset();
    ResetUnits();
    const authenticationToken = await this.http.getAccessToken({
      username,
      password,
      captchaResponse,
      captchaAttempts,
    });
    if (!!authenticationToken) {
      localStorage['atk'] = authenticationToken;
      await this.loadCurrentUserFromServer();
    }
  }
  async socialSignIn({ provider, token }) {
    this.userSavedDataLoaded = undefined;
    this.userSavedDataLoadingPromise = undefined;
    UserStore.reset();
    ResetUnits();
    const authenticationToken = await this.http.getSocialAccessToken({
      provider,
      token,
    });
    if (!!authenticationToken) {
      localStorage['atk'] = authenticationToken;
      await this.loadCurrentUserFromServer();
    }
  }

  async signOut() {
    this._currentUser = undefined;
    this.userSavedDataLoaded = undefined;
    this.userSavedDataLoadingPromise = undefined;
    UserStore.reset();
    ResetUnits();
    try {
      if (!!localStorage.getItem('atk')) {
        await this.http.signOutFromServer();
      }
    } catch (e) {}
    localStorage.removeItem('atk');
  }
  sendVerificationCodes = async (verificationFields) => await this.http.sendVerificationCodes(verificationFields);
  verifySignUpCode = async (verification) => await this.http.verifySignUpCode(verification);

  signUp = async (user) => {
    await this.http.signUp(user);
    // user.username = user.nic || user.passport || user.email;
    user.username = user.email;
    this.userSavedDataLoaded = undefined;
    this.userSavedDataLoadingPromise = undefined;
    UserStore.reset();
    ResetUnits();
    await this.signIn(user);
  };

  async userSavedData(userId?) {
    if (this.userSavedDataLoaded) {
      return this.userSavedDataLoaded;
    }
    if (this.userSavedDataLoadingPromise) {
      return this.userSavedDataLoadingPromise;
    }
    this.userSavedDataLoadingPromise = this.http.userSavedData(userId);
    this.userSavedDataLoaded = await this.userSavedDataLoadingPromise;
    // if (this.userSavedDataLoaded) {
    //   this.userSavedDataLoaded.bankAccounts
    //     .filter((i) => !i.bankCode || !i.bankName)
    //     .forEach((item) => {
    //       this.userSavedDataLoaded.bankAccounts.splice(this.userSavedDataLoaded.bankAccounts.indexOf(item), 1);
    //     });
    //   setTimeout(() => {
    //     this.userSavedDataLoaded = this.userSavedDataLoadingPromise = undefined;
    //   }, 1000 * 5);
    // }
    return this.userSavedDataLoaded;
  }

  sendPasswordReset = async (verificationFields) => await this.http.sendPasswordReset(verificationFields);
  changePassword = async (password) => await this.http.changePassword(password);
  sendEmailConfirmation = async (email) => await this.http.sendEmailConfirmation(email);
  sendMobileConfirmation = async (data) => await this.http.sendMobileConfirmation(data);
  verifyEmailConfirmation = async (verificationCode) => await this.http.verifyEmailConfirmation(verificationCode);
  verifyMobileConfirmation = async (formData) => await this.http.verifyMobileConfirmation(formData);
  updateUserSettings = async (userSettings) => await this.http.updateUserSettings(userSettings);
  async getUserSettings() {
    return await this.http.getUserSettings();
  }

  requestResetPassword = async (userInfo) => await this.http.requestResetPassword(userInfo);
  resetPassword = async (userInfo) => {
    await this.http.resetPassword(userInfo);
    userInfo.username = userInfo.nic || userInfo.passport || userInfo.email;
    await this.signIn(userInfo);
  };

  isValidPasswordResetToken = async (resetToken) => await this.http.isValidPasswordResetToken(resetToken);

  async confirmResetPassword({ resetToken, password }) {
    await this.http.confirmResetPassword({ resetToken, password });
    // localStorage[this.accessTokenKey] = accessToken;
    return this.loadCurrentUserFromServer();
  }

  confirmSignupEmail = async (inviteToken) => await this.http.confirmSignupEmail(inviteToken);
  updateProfile = async (user) => await this.http.updateProfile(user);
  updateAvatar = async (avatar) => await this.http.updateAvatar(avatar);
  resendConfirmationEmail = async (email) => await this.http.confirmSignupEmail(email);

  isEmailInUse = async (email) => await this.http.isEmailInUse(email);

  isSystemAdmin() {
    return this.currentUser.systemRoles.indexOf('ROLE_SYS_ADMIN') >= 0;
  }

  isOrgAdmin() {
    return this.currentUser.orgRoles.indexOf('ROLE_ORG_ADMIN') >= 0;
  }

  async accountBalance() {
    let { 'account-balances': accountBalance } = await this.http.getAccountBalance();
    return accountBalance;
  }

  async getFinanceClientDetails(division, accountType, partyId) {
    return await this.http.getFinanceClientDetails(division, accountType, partyId);
  }

  async getAccountFinBalances(customerId = 0) {
    return await this.http.getAccountFinBalances(customerId);
  }

  async getPortfolioBalances(accountType, partyId, division, page, size, sort) {
    return await this.http.getPortfolioBalances(accountType, partyId, division, page, size, sort);
  }

  async getPortfolioBalanceCount(accountType, partyId, division) {
    return await this.http.getPortfolioBalanceCount(accountType, partyId, division);
  }

  async getTransactionBalances(accountType, customerId, division, stockCode, fromDate, toDate, page, size, sort) {
    return await this.http.getTransactionsBalances(accountType, customerId, division, stockCode, fromDate, toDate, page, size, sort);
  }

  async getCALSCompanies(accountType, partyId, division) {
    return await this.http.getCALSCompanies(accountType, partyId, division);
  }

  async getTransactionBalanceCount(accountType, customerId, division, stockCode, fromDate, toDate) {
    return await this.http.getTransactionsBalanceCount(accountType, customerId, division, stockCode, fromDate, toDate);
  }

  async getCALITransactionBalances(accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) {
    return await this.http.getCALITransactionsBalances(
      accountType,
      partyId,
      division,
      fundCode,
      subAccount,
      fromDate,
      toDate,
      page,
      size,
      sort
    );
  }
  async getCALIEarnings(accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) {
    return await this.http.getCALIEarnings(accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort);
  }

  async getCALITransactionBalanceCount(accountType, partyId, division, fundCode, subAccount, fromDate, toDate) {
    return await this.http.getCALITransactionsBalanceCount(accountType, partyId, division, fundCode, subAccount, fromDate, toDate);
  }

  async getCALIGain(accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort) {
    return await this.http.getCALIGain(accountType, partyId, division, fundCode, subAccount, fromDate, toDate, page, size, sort);
  }

  async getCALIGainCount(accountType, partyId, division, fundCode, subAccount, fromDate, toDate) {
    return await this.http.getCALIGainCount(accountType, partyId, division, fundCode, subAccount, fromDate, toDate);
  }

  async getCALTPortfolioBalances(accountType, partyId, division, instrumentActType, page, size, sort) {
    return await this.http.getCALTPortfolioBalances(accountType, partyId, division, instrumentActType, page, size, sort);
  }

  async getCALTPortfolioBalanceCount(accountType, partyId, division, instrumentActType) {
    return await this.http.getCALTPortfolioBalanceCount(accountType, partyId, division, instrumentActType);
  }

  async getCALTPortfolioBalanceTotals(accountType, partyId) {
    return await this.http.getCALTPortfolioBalanceTotals(accountType, partyId);
  }
  async getPortfolioGraphData() {
    return await this.http.getPortfolioGraphData();
  }
  async getProductPortfolioGraphData(product, accType, crmId?) {
    return await this.http.getProductPortfolioGraphData(product, accType, crmId);
  }
  async getSignUpPermission() {
    return this.http.getSignUpPermission();
  }
  async getAccountSummary(id, accType = 'INDIVIDUAL', displayAllFunds = false) {
    return this.http.getAccountSummary(id, accType, displayAllFunds);
  }

  async getAnnualizedYield(crmId, fundCode, subAcc, accType = 'INDIVIDUAL', date) {
    return this.http.getAnnualizedYield(crmId, fundCode, subAcc, accType, date);
  }
  async getDayendDelayedId() {
    return this.http.getDayendDelayedId();
  }

  isAvailableUser(userId, type) {
    return this.http.isAvailableUser(userId, type);
  }

  saveBrowserInfo(data) {
    return this.http.saveBrowserInfo(data);
  }

  hasPrivilege(privilege) {
    if (this._currentUser && this._currentUser.priviledges) {
      return this._currentUser.priviledges.find((element) => {
        return element === privilege;
      });
    }
  }
  getClientRequest() {
    return this.http.getClientRequest();
  }
  submitClientRequest(type, accType, formData) {
    return this.http.submitClientRequest(type, accType, formData);
  }

  getLatestTransaction(crmCode, division) {
    return this.http.getLatestTransaction(crmCode, division);
  }
  getLedgerSummary(fromDate, toDate, investorCode?) {
    return this.http.getLedgerSummary(fromDate, toDate, investorCode);
  }
  getPortfolioBalance(date, investorCode?) {
    return this.http.getPortfolioBalance(date, investorCode);
  }

  getMarginTradePortfolio(date, investorCode?) {
    return this.http.getMarginTradePortfolio(date, investorCode);
  }
  async sendLoginOTP(emailOnly?) {
    return await this.http.sendLoginOTP(emailOnly);
  }
  savePIN(data, isCrmxUser?) {
    return this.http.savePIN(data, isCrmxUser);
  }
  updatePIN(data, isCrmxUser?) {
    return this.http.updatePIN(data, isCrmxUser);
  }
  validatePIN(pin, isCrmxUser?) {
    return this.http.validatePIN(pin, isCrmxUser);
  }
  getUserNickNameAvailability(nickName: string) {
    return this.http.getUserNickNameAvailability(nickName);
  }
  submitNickName(data: any) {
    return this.http.addNickName(data);
  }
  getServerTime() {
    return this.http.getServerTime();
  }
}
